import axios from "axios";
import { recaptchaHeaders } from "@/lib/recaptcha";
import { openUpgradeModal } from "@/lib/upgradeModal";
import { getOrCreateDeviceId } from "@/lib/deviceId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Stamp every outbound API call with the long-lived browser device id.
// Powers the Trusted Devices feature (server checks this header when
// deciding whether to suppress the new-country login alert).
api.interceptors.request.use((config) => {
    try {
        const did = getOrCreateDeviceId();
        if (did) {
            config.headers = config.headers || {};
            config.headers["X-Device-Id"] = did;
        }
    } catch {
        /* never block a request over device-id stamping */
    }
    return config;
});

// Global 402 → upgrade-modal interceptor.
// Any API endpoint that returns 402 Payment Required automatically pops the
// shared upgrade modal. Backend responses can include `feature`, `min_tier`,
// `reason`, and `title` fields in the response body to customise the modal;
// fallbacks are baked into UpgradePromptModal for unknown features.
api.interceptors.response.use(
    (resp) => resp,
    (err) => {
        if (err?.response?.status === 402) {
            const body = err.response.data || {};
            // FastAPI HTTPException puts the dict under `detail`. Support
            // both `{detail: {...}}` and `{...}` shapes.
            const hint = (body && typeof body.detail === "object")
                ? body.detail
                : (typeof body === "object" ? body : {});
            openUpgradeModal({
                feature: hint.feature || hint.feature_id || undefined,
                minTier: hint.min_tier || hint.minTier || undefined,
                reason: hint.reason || (typeof body.detail === "string" ? body.detail : undefined),
                title: hint.title || undefined,
                source: "api_402",
            });
        }
        return Promise.reject(err);
    },
);

export async function fetchProducts() {
    const r = await api.get("/products");
    return r.data.products;
}

export async function fetchSampleReport() {
    const r = await api.get("/sample-report");
    return r.data.report;
}

export async function fetchArticles(category) {
    const r = await api.get("/learn/articles", {
        params: category ? { category } : {},
    });
    return r.data.articles;
}

export async function fetchArticle(slug) {
    try {
        const r = await api.get(`/learn/articles/${slug}`);
        return r.data;
    } catch (err) {
        // 404 body still carries "Did you mean…" suggestions for the SPA.
        if (err?.response?.status === 404 && err.response.data) {
            return err.response.data;
        }
        throw err;
    }
}

export async function fetchReport(reportId) {
    const r = await api.get(`/reports/${reportId}`);
    return r.data;
}

export async function regenerateReport(reportId) {
    const r = await api.post(`/reports/${reportId}/generate`);
    return r.data;
}

export async function deleteReport(reportId) {
    const r = await api.delete(`/reports/${reportId}`);
    return r.data;
}

export async function updateAccountProfile(payload) {
    const r = await api.patch("/account/profile", payload);
    return r.data.user;
}

export async function updateNotificationPrefs(prefs) {
    // Accept either a boolean (legacy: just the email opt-in) or a full prefs
    // object {email_opt_in, weekly_digest_opt_in}.
    const body =
        typeof prefs === "boolean" ? { email_opt_in: prefs } : prefs;
    const r = await api.patch("/account/notifications", body);
    return r.data.user;
}

export async function requestAccountDeletion(confirmation) {
    const r = await api.post("/account/deletion-request", { confirmation });
    return r.data;
}

export async function getMyDeletionRequest() {
    const r = await api.get("/account/deletion-request");
    return r.data.request;
}

export async function exportMyData() {
    const r = await api.get("/account/export");
    return r.data;
}

export async function updateUploadZip(upload_id, zip_code) {
    const r = await api.patch(`/uploads/${upload_id}/zip`, { zip_code });
    return r.data;
}

export async function listDashboardReports() {
    const r = await api.get("/dashboard/reports");
    return r.data.reports;
}

export async function listDashboardUploads() {
    const r = await api.get("/dashboard/uploads");
    return r.data.uploads;
}

export async function dashboardQuota() {
    const r = await api.get("/dashboard/quota");
    return r.data.quota;
}

export async function fetchInvestorTier() {
    // Public payload; returns tier=null for anonymous users.
    const r = await api.get("/investor/tier");
    return r.data; // { tier: {...}, usage: {...} | null }
}

// ===== Phase 2 — Investor workflow =====
export async function listInvestorProperties() {
    const r = await api.get("/investor/properties");
    return r.data; // { properties, tier, pipeline_statuses, pipeline_labels }
}

export async function updateInvestorProperty(propertyId, patch) {
    const r = await api.patch(`/investor/properties/${propertyId}`, patch);
    return r.data.property;
}

export async function searchInvestorReports(query) {
    const r = await api.get("/investor/search", { params: { q: query } });
    return r.data; // { query, results, result_count }
}

export function investorComparisonCsvUrl() {
    return `${API_BASE}/investor/comparison.csv`;
}

export async function listInvestorNotes({ report_id, property_id } = {}) {
    const r = await api.get("/investor/notes", {
        params: { report_id, property_id },
    });
    return r.data; // { notes, locked, min_tier }
}

export async function createInvestorNote(payload) {
    const r = await api.post("/investor/notes", payload);
    return r.data.note;
}

export async function updateInvestorNote(noteId, patch) {
    const r = await api.patch(`/investor/notes/${noteId}`, patch);
    return r.data.note;
}

export async function deleteInvestorNote(noteId) {
    const r = await api.delete(`/investor/notes/${noteId}`);
    return r.data;
}

export async function fetchInspectionRiskScore(reportId) {
    const r = await api.get(`/investor/risk-score/${reportId}`);
    return r.data.risk_score;
}

// ===== Phase 3 — Saved Negotiation Summaries / Quote Tracking / Calibration =====
export async function listNegotiationSummaries(reportId) {
    const r = await api.get("/investor/negotiation-summaries", {
        params: { report_id: reportId },
    });
    return r.data; // { summaries, locked, min_tier }
}

export async function createNegotiationSummary(payload) {
    const r = await api.post("/investor/negotiation-summaries", payload);
    return r.data.summary;
}

export async function updateNegotiationSummary(summaryId, patch) {
    const r = await api.patch(`/investor/negotiation-summaries/${summaryId}`, patch);
    return r.data.summary;
}

export async function deleteNegotiationSummary(summaryId) {
    const r = await api.delete(`/investor/negotiation-summaries/${summaryId}`);
    return r.data;
}

export async function listQuoteTracking({ status, report_id } = {}) {
    const r = await api.get("/investor/quote-tracking", {
        params: { status, report_id },
    });
    return r.data; // { items, counts, statuses, locked, min_tier }
}

export async function createQuoteTracking(payload) {
    const r = await api.post("/investor/quote-tracking", payload);
    return r.data.item;
}

export async function updateQuoteTracking(qid, patch) {
    const r = await api.patch(`/investor/quote-tracking/${qid}`, patch);
    return r.data.item;
}

export async function deleteQuoteTracking(qid) {
    const r = await api.delete(`/investor/quote-tracking/${qid}`);
    return r.data;
}

export async function listExposureActuals(reportId) {
    const r = await api.get("/investor/exposure-actuals", {
        params: { report_id: reportId },
    });
    return r.data; // { items, summary, locked, min_tier }
}

export async function createExposureActual(payload) {
    const r = await api.post("/investor/exposure-actuals", payload);
    return r.data.item;
}

export async function deleteExposureActual(aid) {
    const r = await api.delete(`/investor/exposure-actuals/${aid}`);
    return r.data;
}

export async function previewWeeklyDigest() {
    const r = await api.get("/investor/digest/preview");
    return r.data;
}

// ===== Phase 4 — Plus polish =====
export async function listContractorNotes() {
    const r = await api.get("/investor/contractor-notes");
    return r.data; // { notes, locked, min_tier }
}

export async function createContractorNote(payload) {
    const r = await api.post("/investor/contractor-notes", payload);
    return r.data.note;
}

export async function updateContractorNote(cnid, patch) {
    const r = await api.patch(`/investor/contractor-notes/${cnid}`, patch);
    return r.data.note;
}

export async function deleteContractorNote(cnid) {
    const r = await api.delete(`/investor/contractor-notes/${cnid}`);
    return r.data;
}

export async function listBulkUploads() {
    const r = await api.get("/investor/bulk-uploads");
    return r.data; // { jobs, counts, statuses, locked, min_tier }
}

export async function enqueueBulkUploads(files) {
    // files: [{file_name, file_size}]
    const r = await api.post("/investor/bulk-uploads", { files });
    return r.data.jobs;
}

export async function updateBulkUploadJob(bjid, patch) {
    const r = await api.patch(`/investor/bulk-uploads/${bjid}`, patch);
    return r.data.job;
}

export async function deleteBulkUploadJob(bjid) {
    const r = await api.delete(`/investor/bulk-uploads/${bjid}`);
    return r.data;
}

export function portfolioExportZipUrl() {
    return `${API_BASE}/investor/exports/portfolio.zip`;
}

export async function fetchCalibrationShareStats() {
    const r = await api.get("/investor/calibration-share/stats");
    return r.data;
}

export async function fetchInvestorTeamInfo() {
    const r = await api.get("/investor/team");
    return r.data;
}

// ===== Phase 5 — Reviews & Feedback =====
export async function fetchReviewEligibility(reportId) {
    const r = await api.get(`/feedback/eligibility/${reportId}`);
    return r.data; // { eligible, reason, ...ctx }
}

export async function submitReview(payload, recaptchaToken) {
    const headers = {};
    if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
    const r = await api.post("/feedback/review", payload, { headers });
    return r.data.review;
}

export async function submitReportFeedback(payload) {
    const r = await api.post("/feedback/report", payload);
    return r.data;
}

export async function submitIssueFeedback(payload) {
    const r = await api.post("/feedback/issue", payload);
    return r.data;
}

export async function submitExposureFeedback(payload) {
    const r = await api.post("/feedback/exposure", payload);
    return r.data;
}

export async function submitFeatureRequest(payload, recaptchaToken) {
    const headers = {};
    if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
    const r = await api.post("/feedback/feature-request", payload, { headers });
    return r.data;
}

export async function adminFeedbackList(params) {
    const r = await api.get("/admin/feedback", { params });
    return r.data;
}

export async function adminFeedbackPatch(sourceType, recordId, patch) {
    const r = await api.patch(
        `/admin/feedback/${sourceType}/${recordId}`,
        patch,
    );
    return r.data.item;
}

export async function adminFeedbackConvertTicket(sourceType, recordId) {
    const r = await api.post(
        `/admin/feedback/${sourceType}/${recordId}/convert-ticket`,
    );
    return r.data.ticket;
}

export async function adminFeedbackTopThemes(days = 7, limit = 8) {
    const r = await api.get("/admin/feedback/top-themes", {
        params: { days, limit },
    });
    return r.data;
}

export async function adminFeedbackTriage(aging_days = 7, limit = 50, audience_segment = null) {
    const params = { aging_days, limit };
    if (audience_segment) params.audience_segment = audience_segment;
    const r = await api.get("/admin/feedback/triage", { params });
    return r.data;
}

export async function adminSupportOutstandingCount(opts = {}) {
    const params = {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/support/outstanding-count", { params });
    return r.data;
}

export async function adminMarkContactStatus(message_id, status) {
    const r = await api.patch(`/admin/contacts/${message_id}`, { status });
    return r.data;
}

export async function adminPreflightSmokeTest() {
    const r = await api.post("/admin/preflight/smoke-test", null, { timeout: 30000 });
    return r.data;
}

export async function adminPreflightHistory(days = 7) {
    const r = await api.get("/admin/preflight/history", { params: { days } });
    return r.data;
}

export async function adminIssuesSnapshot({ window_days = 7, log_lines = 30 } = {}) {
    const r = await api.get("/admin/issues/snapshot", {
        params: { window_days, log_lines },
        timeout: 20000,
    });
    return r.data;
}

export async function adminTestDriftAlert(config = {}) {
    const r = await api.post("/admin/preflight/test-drift-alert", config, { timeout: 20000 });
    return r.data;
}

export async function adminOpsRunbook(tripwire_id) {
    const r = await api.get(`/admin/ops/runbook/${tripwire_id}`);
    return r.data;
}

export async function adminGitStatus() {
    const r = await api.get("/admin/git-status");
    return r.data;
}

export async function adminBillingTestStart() {
    const r = await api.post("/admin/billing-test/start", null, { timeout: 20000 });
    return r.data;
}

export async function adminBillingTestRecent() {
    const r = await api.get("/admin/billing-test/recent");
    return r.data;
}

export async function adminDayZeroResetPreview() {
    const r = await api.get("/admin/ops/day-zero-reset/preview", { timeout: 30000 });
    return r.data;
}

export async function adminDayZeroResetExecute({ confirm_text, password }) {
    const r = await api.post(
        "/admin/ops/day-zero-reset/execute",
        { confirm_text, password },
        { timeout: 300000 },  // mongodump + 60+ collection wipes can take a minute
    );
    return r.data;
}

export async function adminOpsListBackups() {
    const r = await api.get("/admin/ops/backups");
    return r.data;
}

export async function adminOpsBackupRunNow() {
    const r = await api.post("/admin/ops/backups/run-now", null, { timeout: 180000 });
    return r.data;
}

export async function adminOpsBackupDrDrill() {
    const r = await api.post("/admin/ops/backups/dr-drill", null, { timeout: 180000 });
    return r.data;
}

export async function adminOpsBackupDrDrillLatest() {
    const r = await api.get("/admin/ops/backups/dr-drill/latest");
    return r.data;
}

export async function adminInactiveWipePreview() {
    const r = await api.get("/admin/ops/inactive-wipe/preview");
    return r.data;
}

export async function adminInactiveWipeExecute(body) {
    const r = await api.post("/admin/ops/inactive-wipe/execute", body, {
        timeout: 180000,
    });
    return r.data;
}

export async function adminStuckUsersList(limit = 200) {
    const r = await api.get("/admin/stuck-users", { params: { limit } });
    return r.data;
}

export async function adminStuckUsersDryRun() {
    const r = await api.post("/admin/stuck-users/dry-run");
    return r.data;
}

export async function adminStuckUsersRunDrip() {
    const r = await api.post("/admin/stuck-users/run-drip", null, {
        timeout: 120000,
    });
    return r.data;
}

export async function adminStuckUsersResend(body) {
    const r = await api.post("/admin/stuck-users/resend", body);
    return r.data;
}

export async function adminUrlSources() {
    const r = await api.get("/admin/url-sources");
    return r.data;
}

export async function adminSmokeTestUrls() {
    const r = await api.post("/admin/smoke-test-urls");
    return r.data;
}

export async function adminOpsStatus() {
    const r = await api.get("/admin/ops/status");
    return r.data;
}

export async function adminOpsListHeroes() {
    const r = await api.get("/admin/ops/heroes/list");
    return r.data;
}

export async function adminOpsRegenerateHeroes() {
    const r = await api.post("/admin/ops/heroes/regenerate-all", null, { timeout: 180000 });
    return r.data;
}

export async function adminOpsListHeroPrompts() {
    const r = await api.get("/admin/ops/heroes/prompts");
    return r.data;
}

export async function adminOpsSaveHeroPrompt(hero_key, prompt) {
    const r = await api.put(`/admin/ops/heroes/prompts/${hero_key}`, { prompt });
    return r.data;
}

export async function adminOpsResetHeroPrompt(hero_key) {
    const r = await api.post(`/admin/ops/heroes/prompts/${hero_key}/reset`);
    return r.data;
}

export async function adminOpsRegenerateOneHero(hero_key) {
    const r = await api.post(`/admin/ops/heroes/${hero_key}/regenerate`, null, { timeout: 120000 });
    return r.data;
}

export async function adminOpsListHeroRevisions(hero_key) {
    const r = await api.get(`/admin/ops/heroes/prompts/${hero_key}/revisions`);
    return r.data;
}

export async function adminOpsRestoreHeroRevision(hero_key, revision_id) {
    const r = await api.post(
        `/admin/ops/heroes/prompts/${hero_key}/revisions/${revision_id}/restore`,
    );
    return r.data;
}

export async function adminOpsRevertHeroPng(hero_key) {
    const r = await api.post(`/admin/ops/heroes/${hero_key}/revert-png`);
    return r.data;
}

export async function adminFunnelLaunch(days = 30, opts = {}) {
    const params = { days };
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/funnel/launch", { params });
    return r.data;
}

export async function adminRestoreListR2() {
    const r = await api.get("/admin/ops/restore-from-r2/list");
    return r.data;
}

export async function adminRestorePreview(s3_key) {
    const r = await api.post(
        "/admin/ops/restore-from-r2/preview",
        { s3_key },
        { timeout: 60000 },
    );
    return r.data;
}

export async function adminRestoreExecute({ s3_key, confirm_text, password }) {
    const r = await api.post(
        "/admin/ops/restore-from-r2/execute",
        { s3_key, confirm_text, password },
        { timeout: 300000 },
    );
    return r.data;
}

export async function submitMicrosurvey(payload) {
    const r = await api.post("/feedback/microsurvey", payload);
    return r.data;
}

export async function adminMicrosurveyList(params = {}) {
    const r = await api.get("/admin/microsurvey", { params });
    return r.data;
}

export async function adminPipelineHealth() {
    const r = await api.get("/admin/pipeline-health");
    return r.data;
}

export async function adminBackfillAudienceSegments() {
    const r = await api.post("/admin/audience-insights/backfill-segments");
    return r.data;
}

export async function switchToAnnual() {
    const r = await api.post("/subscriptions/switch-to-annual");
    return r.data;
}

export async function getAdminSubscriptionAnalytics() {
    const r = await api.get("/admin/subscription-analytics");
    return r.data;
}

export async function getAdminSubscriptionEconomics() {
    const r = await api.get("/admin/subscription-economics");
    return r.data;
}

export async function getAdminLifecycleFunnelEvents(touchpoint, { limit = 50, days = 30 } = {}) {
    const r = await api.get("/admin/subscription-analytics/lifecycle-funnel/events", {
        params: { touchpoint, limit, days },
    });
    return r.data;
}

// ----- Support v2 -----
export async function fetchSupportBanner() {
    const r = await api.get("/support-banner");
    return r.data.banner;
}

export async function adminUpsertSupportBanner(payload) {
    const r = await api.put("/admin/support/banner", payload);
    return r.data.banner;
}

export async function adminListCannedResponses() {
    const r = await api.get("/admin/support/canned-responses");
    return r.data.canned_responses;
}

export async function adminCreateCannedResponse(payload) {
    const r = await api.post("/admin/support/canned-responses", payload);
    return r.data.canned_response;
}

export async function adminUpdateCannedResponse(id, payload) {
    const r = await api.patch(`/admin/support/canned-responses/${id}`, payload);
    return r.data.canned_response;
}

export async function adminDeleteCannedResponse(id) {
    const r = await api.delete(`/admin/support/canned-responses/${id}`);
    return r.data;
}

export async function adminOpenRefund(ticketId, payload) {
    const r = await api.post(`/admin/support/tickets/${ticketId}/refund`, payload);
    return r.data;
}

export async function adminListRefunds(params = {}) {
    const r = await api.get("/admin/refund-requests", { params });
    return r.data;
}

export async function adminUpdateRefund(id, patch) {
    const r = await api.patch(`/admin/refund-requests/${id}`, patch);
    return r.data.refund_request;
}

export async function uploadCmsImage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/admin/cms/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
}

export async function fetchShippedFeed(limit = 5) {
    const r = await api.get("/feedback/shipped-feed", { params: { limit } });
    return r.data.items;
}



export async function myEntitlements() {
    const r = await api.get("/entitlements/me");
    return r.data;
}

export async function createCheckout(productId, uploadId) {
    const origin_url = window.location.origin;
    const r = await api.post("/payments/checkout/session", {
        product_id: productId,
        upload_id: uploadId || null,
        origin_url,
    });
    return r.data;
}

export async function checkoutStatus(sessionId) {
    const r = await api.get(`/payments/checkout/status/${sessionId}`);
    return r.data;
}

export async function uploadPdf(file, persona, zipCode, opts = {}) {
    const form = new FormData();
    form.append("file", file);
    if (persona) form.append("persona", persona);
    if (zipCode) form.append("zip_code", zipCode);
    if (opts.dealStage) form.append("deal_stage", opts.dealStage);
    if (opts.urgency) form.append("urgency", opts.urgency);
    const captchaHeaders = await recaptchaHeaders("upload_start");
    const r = await axios.post(`${API_BASE}/uploads`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", ...captchaHeaders },
    });
    return r.data;
}

export async function claimUpload(uploadId) {
    const r = await api.post(`/uploads/${uploadId}/claim`);
    return r.data;
}

export async function submitContact(payload) {
    const headers = await recaptchaHeaders("contact_submit");
    const r = await api.post("/contact", payload, { headers });
    return r.data;
}

export async function submitQuote(payload) {
    const headers = await recaptchaHeaders("quote_request_submit");
    const r = await api.post("/quotes", payload, { headers });
    return r.data;
}

export async function listMyQuotes() {
    const r = await api.get("/quotes/mine");
    return r.data.quotes;
}

export async function cancelMyQuote(quoteId) {
    const r = await api.delete(`/quotes/${quoteId}`);
    return r.data;
}

export function adminExportQuotesCsvUrl(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/admin/quotes/export.csv${qs ? `?${qs}` : ""}`;
}

export async function adminOverview(opts = {}) {
    const params = {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/overview", { params });
    return r.data;
}
export async function adminUsers(q, opts = {}) {
    const params = q ? { q } : {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/users", { params });
    return r.data.users;
}
export async function adminUploads() {
    const r = await api.get("/admin/uploads");
    return r.data.uploads;
}
export async function adminReports(opts = {}) {
    const params = {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/reports", { params });
    return r.data.reports;
}
export async function adminTransactions(opts = {}) {
    const params = {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/transactions", { params });
    return r.data.transactions;
}
export async function adminContacts(opts = {}) {
    const params = {};
    if (opts.include_test) params.include_test = true;
    const r = await api.get("/admin/contacts", { params });
    return r.data.contacts;
}
export async function adminQuotes(params = {}) {
    const r = await api.get("/admin/quotes", { params });
    return r.data;
}
export async function adminUpdateQuote(quote_id, body) {
    const r = await api.patch(`/admin/quotes/${quote_id}`, body);
    return r.data.quote;
}
export async function adminEmailOptins() {
    const r = await api.get("/admin/email-optins");
    return r.data;
}

export async function adminAuditLogs() {
    const r = await api.get("/admin/audit-logs");
    return r.data.logs;
}

export async function adminStripeEvents(params = {}) {
    const r = await api.get("/admin/stripe-events", { params });
    return r.data; // { events, counts, total }
}

export async function adminCostCalibration(params = {}) {
    const r = await api.get("/admin/cost-calibration", { params });
    return r.data; // { rows, count }
}

// CMS
export async function adminListArticles(params = {}) {
    const r = await api.get("/admin/articles", { params });
    return r.data; // { articles, counts }
}
export async function adminCreateArticle(payload) {
    const r = await api.post("/admin/articles", payload);
    return r.data.article;
}
export async function adminUpdateArticle(slug, payload) {
    const r = await api.put(`/admin/articles/${slug}`, payload);
    return r.data.article;
}
export async function adminDeleteArticle(slug, opts = {}) {
    const r = await api.delete(`/admin/articles/${slug}`, {
        params: opts.hard ? { hard: true } : undefined,
    });
    return r.data;
}
export async function adminRestoreArticle(slug) {
    const r = await api.post(`/admin/articles/${slug}/restore`);
    return r.data;
}
export async function adminDuplicateArticle(slug) {
    const r = await api.post(`/admin/articles/${slug}/duplicate`);
    return r.data.article;
}
export async function adminCheckSlug({ slug, title }) {
    const r = await api.post("/admin/articles/check-slug", { slug, title });
    return r.data; // { available, suggested, slug }
}
export async function adminAutosaveArticle(slug, autosave_content) {
    const r = await api.post(`/admin/articles/${slug}/autosave`, {
        autosave_content,
    });
    return r.data;
}

// CMS Phase B — revisions + scheduled publishing
export async function adminListArticleRevisions(slug, limit = 25) {
    const r = await api.get(`/admin/articles/${slug}/revisions`, { params: { limit } });
    return r.data.revisions || [];
}

export async function adminGetArticleRevision(slug, revisionId) {
    const r = await api.get(`/admin/articles/${slug}/revisions/${revisionId}`);
    return r.data.revision;
}

export async function adminRestoreArticleRevision(slug, revisionId) {
    const r = await api.post(`/admin/articles/${slug}/revisions/${revisionId}/restore`);
    return r.data;
}
export async function adminRetryExtraction(uploadId) {
    const r = await api.post(`/admin/uploads/${uploadId}/retry-extraction`);
    return r.data;
}
export async function adminRegenerateReport(reportId) {
    const r = await api.post(`/admin/reports/${reportId}/regenerate`);
    return r.data;
}

// Billing
export async function openBillingPortal() {
    const r = await api.post("/billing/portal");
    return r.data;
}

export async function fetchBillingSubscription() {
    const r = await api.get("/billing/subscription");
    return r.data.subscription;
}


// Admin: account deletion requests
export async function adminDeletionRequests(params = {}) {
    const r = await api.get("/admin/deletion-requests", { params });
    return r.data;
}
export async function adminUpdateDeletionRequest(request_id, body) {
    const r = await api.patch(`/admin/deletion-requests/${request_id}`, body);
    return r.data.request;
}
export async function adminDeletedReports() {
    const r = await api.get("/admin/deleted-reports");
    return r.data.reports;
}


// ===== Auth recovery & verification =====
export async function sendVerificationEmail() {
    const headers = await recaptchaHeaders("resend_verification");
    const r = await api.post("/auth/send-verification", {}, { headers });
    return r.data;
}

export async function verifyEmailToken(token) {
    const r = await api.post("/auth/verify-email", { token });
    return r.data;
}

export async function revertEmailChange(token) {
    const r = await api.post("/auth/revert-email-change", { token });
    return r.data;
}

export async function fetchAccountActivity(limit = 50) {
    const r = await api.get("/account/activity", { params: { limit } });
    return r.data;
}

export async function logoutOtherSessions() {
    const r = await api.post("/auth/logout-other-sessions");
    return r.data;
}

export async function fetchActiveSessions() {
    const r = await api.get("/account/sessions");
    return r.data;
}

export async function revokeSession(session_id) {
    const r = await api.post("/account/sessions/revoke", { session_id });
    return r.data;
}

export async function revokeDeviceFromEmailToken(token) {
    const r = await api.post("/auth/login-alert-revoke", { token });
    return r.data;
}

export async function fetchTrustedDevices() {
    const r = await api.get("/account/trusted-devices");
    return r.data;
}

export async function trustCurrentDevice() {
    const r = await api.post("/account/trusted-devices");
    return r.data;
}

export async function untrustDevice(device_id) {
    const r = await api.delete(`/account/trusted-devices/${device_id}`);
    return r.data;
}

export async function requestPasswordReset(email) {
    const headers = await recaptchaHeaders("forgot_password");
    const r = await api.post("/auth/forgot-password", { email }, { headers });
    return r.data;
}

export async function resetPasswordWithToken(token, new_password) {
    const r = await api.post("/auth/reset-password", { token, new_password });
    return r.data;
}

export async function changeAccountPassword(current_password, new_password) {
    const r = await api.post("/account/password", {
        current_password,
        new_password,
    });
    return r.data;
}

export async function changeAccountEmail(new_email, password) {
    const r = await api.post("/account/email", { new_email, password });
    return r.data;
}

// ===== Support =====
export async function supportDiagnose({ report_id, upload_id, payment_id, quote_request_id } = {}) {
    const r = await api.post("/support/diagnose", {
        report_id, upload_id, payment_id, quote_request_id,
    });
    return r.data; // { snapshot, hint }
}

export async function createSupportTicket(payload) {
    const headers = await recaptchaHeaders("support_submit");
    const r = await api.post("/support/tickets", payload, { headers });
    return r.data.ticket;
}

export async function listMySupportTickets() {
    const r = await api.get("/support/tickets/mine");
    return r.data.tickets;
}

export async function restorePurchase({ upload_id, report_id }) {
    const r = await api.post("/support/restore-purchase", { upload_id, report_id });
    return r.data;
}

export async function submitSupportFeedback(payload) {
    const r = await api.post("/support/feedback", payload);
    return r.data;
}

export async function adminListSupportTickets(params = {}) {
    const r = await api.get("/admin/support/tickets", { params });
    return r.data; // { tickets, status_counts, priority_counts }
}

export async function adminGetSupportTicket(ticket_id) {
    const r = await api.get(`/admin/support/tickets/${ticket_id}`);
    return r.data; // { ticket, notes }
}

export async function adminUpdateSupportTicket(ticket_id, patch) {
    const r = await api.patch(`/admin/support/tickets/${ticket_id}`, patch);
    return r.data.ticket;
}

export async function adminAddSupportTicketNote(ticket_id, note) {
    const r = await api.post(`/admin/support/tickets/${ticket_id}/notes`, { note });
    return r.data.note;
}

export async function adminOpenAIUsage(days = 30) {
    const r = await api.get("/admin/openai-usage", { params: { days } });
    return r.data;
}

export async function recoverAccount(emails) {
    const headers = await recaptchaHeaders("recover_account");
    const r = await api.post("/auth/recover-account", { emails }, { headers });
    return r.data;
}

// ===== Per-user report state =====
export async function fetchReportUserState(reportId) {
    const r = await api.get(`/reports/${reportId}/state`);
    return r.data.state_by_issue || {};
}

export async function patchIssueState(reportId, issueId, patch) {
    const r = await api.patch(`/reports/${reportId}/issues/${issueId}/state`, patch);
    return r.data.state;
}


// ===== SEO Tools — Cost-Safe rule-based calculators =====

/**
 * Optional session storage for a completed tool run. Backend stores a
 * lightweight summary used by the admin /admin/tools overview. Failure to
 * persist must NEVER block the user experience — call sites should swallow
 * errors silently.
 */
export async function recordToolSession({ tool_name, inputs, result, zip_code }) {
    const r = await api.post("/tools/sessions", {
        tool_name,
        inputs: inputs || {},
        result: result || {},
        zip_code: zip_code || null,
    });
    return r.data;
}

/**
 * "Email me my results" — captures email + optional marketing consent.
 * Backend handles reCAPTCHA verification + Resend send + tool_leads upsert.
 */
export async function submitToolLead(payload) {
    const headers = await recaptchaHeaders("tool_email_results");
    const r = await api.post("/tools/leads", payload, { headers });
    return r.data;
}

// ===== Cohort 1: anonymous-visitor lead capture =====

/** Email-gated Buyer's Checklist PDF download. */
export async function captureBuyersChecklist(payload) {
    const headers = await recaptchaHeaders("lead_capture_checklist");
    const r = await api.post("/lead-capture/buyers-checklist", payload, { headers });
    return r.data;
}

/** Soft-paywall unlock on the 2nd free calculator run. */
export async function captureSoftPaywall(payload) {
    const headers = await recaptchaHeaders("lead_capture_paywall");
    const r = await api.post("/lead-capture/soft-paywall", payload, { headers });
    return r.data;
}

export async function adminListLeadCaptures(params = {}) {
    const r = await api.get("/admin/lead-captures", { params });
    return r.data;
}

// ===== Cohort 1.1: Test feedback / support scrub =====

export async function adminTestFeedbackScrubPreview() {
    const r = await api.get("/admin/ops/test-feedback-scrub/preview");
    return r.data;
}

export async function adminTestFeedbackScrubExecute() {
    const r = await api.post("/admin/ops/test-feedback-scrub/execute", {
        confirm_text: "WIPE",
    });
    return r.data;
}

// ===== External WCAG 2.2 AA audit cadence =====

export async function adminWcagAuditGet() {
    const r = await api.get("/admin/ops/wcag-audit");
    return r.data;
}

export async function adminWcagAuditUpdate({ audit_date, auditor, report_url }) {
    const r = await api.post("/admin/ops/wcag-audit", {
        audit_date,
        auditor,
        report_url,
    });
    return r.data;
}

// ===== Automated WCAG 2.2 AA static scanner =====

export async function adminWcagStaticAuditRun({ base_url } = {}) {
    const r = await api.post("/admin/ops/wcag-static-audit/run", {
        base_url: base_url || undefined,
    });
    return r.data;
}

export async function adminWcagStaticAuditLatest() {
    const r = await api.get("/admin/ops/wcag-static-audit/latest");
    return r.data;
}

export async function submitLenderEstimate(payload) {
    const headers = await recaptchaHeaders("tool_lender_estimate");
    const r = await api.post("/tools/lender-estimate", payload, { headers });
    return r.data;
}

export async function adminToolsOverview() {
    const r = await api.get("/admin/tools/overview");
    return r.data;
}

export async function adminToolsRecoveryStats(window_days = 30) {
    const r = await api.get("/admin/tools/recovery-stats", {
        params: { window_days },
    });
    return r.data;
}

export async function adminToolsRecoveryDrilldown(tool_name, window_days = 30, limit = 100) {
    const r = await api.get("/admin/tools/recovery-drilldown", {
        params: { tool_name, window_days, limit },
    });
    return r.data;
}

// ===== Admin Partner Leads (unified inbox for quote + lender-estimate flows) =====

export async function adminListPartnerLeads(params = {}) {
    const r = await api.get("/admin/partner-leads", { params });
    return r.data;
}

export async function adminRoutePartnerLead(lead_type, lead_id, { routed_to, notes_internal } = {}) {
    const r = await api.post(
        `/admin/partner-leads/${lead_type}/${lead_id}/route`,
        { routed_to, notes_internal },
    );
    return r.data;
}

export async function adminUpdatePartnerLeadStatus(lead_type, lead_id, { status, notes_internal } = {}) {
    const r = await api.post(
        `/admin/partner-leads/${lead_type}/${lead_id}/status`,
        { status, notes_internal },
    );
    return r.data;
}

// ===== Admin Content Engine (AI content package generation) =====

export async function adminListContentPackages(params = {}) {
    const r = await api.get("/admin/content-packages", { params });
    return r.data;
}

export async function adminCreateContentPackage(input) {
    const r = await api.post("/admin/content-packages", input, { timeout: 300000 });
    return r.data.package;
}

export async function adminGetContentPackage(package_id) {
    const r = await api.get(`/admin/content-packages/${package_id}`);
    return r.data.package;
}

export async function adminUpdateContentPackage(package_id, patch) {
    const r = await api.patch(`/admin/content-packages/${package_id}`, patch);
    return r.data.package;
}

export async function adminRegenerateContentSection(package_id, section) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/regenerate/${section}`,
        null,
        { timeout: 300000 },
    );
    return r.data;
}

export async function adminRegenerateHeroImage(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/hero-image/regenerate`,
        null,
        { timeout: 180000 },
    );
    return r.data;
}

export async function adminGenerateMissingAssets(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/assets/generate-missing`,
    );
    return r.data;
}

export async function adminRegenerateAllAssets(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/assets/regenerate-all`,
    );
    return r.data;
}

export async function adminBulkGenerateMissingAssets() {
    const r = await api.post("/admin/content-packages/bulk/generate-missing");
    return r.data;
}

export async function adminBulkRegenerateAllAssets() {
    const r = await api.post("/admin/content-packages/bulk/regenerate-all");
    return r.data;
}

export async function adminBulkRebrandImages() {
    const r = await api.post("/admin/content-packages/bulk/rebrand-images");
    return r.data;
}

export async function adminBulkUnarchiveLegacy() {
    const r = await api.post("/admin/content-packages/bulk/unarchive-legacy");
    return r.data;
}

export async function adminBulkGenerateMissingHeroes() {
    const r = await api.post(
        "/admin/content-packages/bulk/generate-missing-heroes",
    );
    return r.data;
}

export async function adminResetContentGeneration(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/generation/reset`,
    );
    return r.data;
}

export async function getAdminContentEngineHealth() {
    const r = await api.get("/admin/content-engine/health");
    return r.data;
}

export async function getAdminContentEnginePlatformFailures(platform, { limit = 25 } = {}) {
    const r = await api.get(
        `/admin/content-engine/health/platform/${platform}/failures`,
        { params: { limit } },
    );
    return r.data;
}

export async function adminContentEnginePlatformRetryRecent(platform, package_ids) {
    const r = await api.post(
        `/admin/content-engine/health/platform/${platform}/retry-recent`,
        { package_ids },
    );
    return r.data;
}

export async function adminGenerateShortFormVideo(package_id, { regenerate_script = true } = {}) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/short-form-video/generate`,
        null,
        { params: { regenerate_script } },
    );
    return r.data;
}

export async function adminResetShortFormVideo(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/short-form-video/reset`,
    );
    return r.data;
}

export async function adminApproveShortFormVideo(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/short-form-video/approve`,
    );
    return r.data;
}

export async function adminBulkGenerateShortFormVideos() {
    const r = await api.post(
        "/admin/content-packages/bulk/generate-short-form-videos",
    );
    return r.data;
}

export async function adminContentPackageQA(package_id) {
    const r = await api.post(`/admin/content-packages/${package_id}/qa`);
    return r.data;
}

export async function adminPublishContentPackage(package_id) {
    const r = await api.post(`/admin/content-packages/${package_id}/publish`);
    return r.data;
}

export async function adminSendTestEmail(package_id) {
    const r = await api.post(`/admin/content-packages/${package_id}/send-test-email`);
    return r.data;
}

export async function adminNewsletterConversionStats() {
    const r = await api.get("/admin/newsletter/conversion-stats");
    return r.data;
}

export async function adminArchiveContentPackage(package_id) {
    const r = await api.delete(`/admin/content-packages/${package_id}`);
    return r.data;
}

export function adminContentInfographicSvgUrl(package_id) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/admin/content-packages/${package_id}/infographic.svg`;
}

export function adminContentImageUrl(package_id, filename) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/admin/content-packages/${package_id}/image/${filename}`;
}

// V1 Lean Engine — 9-image gallery (1 infographic + 8 Pinterest pins).
// All image-asset endpoints live at /api/admin/content-packages/{id}/images.
export async function adminListPackageImages(package_id) {
    const r = await api.get(`/admin/content-packages/${package_id}/images`);
    return r.data;
}

export async function adminImageProgress(package_id) {
    const r = await api.get(`/admin/content-packages/${package_id}/images/progress`);
    return r.data;
}

export async function adminGenerateAllImages(package_id, { force = false } = {}) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/generate-all`,
        null,
        { params: { force } },
    );
    return r.data;
}

export async function adminRegenerateImageAsset(package_id, asset_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/${asset_id}/regenerate`,
        null,
        { timeout: 120000 },
    );
    return r.data;
}

export async function adminApproveImageAsset(package_id, asset_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/${asset_id}/approve`,
    );
    return r.data;
}

export async function adminRejectImageAsset(package_id, asset_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/${asset_id}/reject`,
    );
    return r.data;
}

export async function adminBulkApproveImages(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/bulk-approve`,
    );
    return r.data;
}

export async function adminQAAutoFix(package_id, { dryRun = false } = {}) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/qa-fix`,
        null,
        { params: { dry_run: dryRun } },
    );
    return r.data;
}

export async function adminBulkQAAutoFix({ dryRun = false } = {}) {
    const r = await api.post(
        `/admin/content-packages/bulk/qa-fix`,
        null,
        { params: { dry_run: dryRun }, timeout: 120000 },
    );
    return r.data;
}

export async function adminImageStatusOverview() {
    const r = await api.get("/admin/content-engine/image-status");
    return r.data;
}

export async function adminUniversalKeyBalance() {
    const r = await api.get("/admin/llm/universal-key-balance");
    return r.data;
}

export async function adminArticlePreview(package_id) {
    const r = await api.get(`/admin/content-packages/${package_id}/article-preview`);
    return r.data;
}

export function publicImageUrl(asset_id) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/content-images/${asset_id}`;
}

// Metricool / Pinterest one-click distribution (V1 image pipeline →
// scheduled pins). Replaced Buffer integration 2026-02.
export async function adminMetricoolStatus() {
    const r = await api.get("/admin/metricool/status");
    return r.data;
}

export async function adminMetricoolSelectBlog(blog_id) {
    const r = await api.post("/admin/metricool/select-blog", { blog_id });
    return r.data;
}

export async function adminMetricoolListPinterestBoards() {
    const r = await api.get("/admin/metricool/pinterest/boards");
    return r.data;
}

export async function adminMetricoolGetBoardMappings() {
    const r = await api.get("/admin/metricool/board-mappings");
    return r.data;
}

export async function adminMetricoolPutBoardMappings(mappings) {
    const r = await api.put("/admin/metricool/board-mappings", { mappings });
    return r.data;
}

export async function adminSendPackageToPinterest(package_id, opts = {}) {
    // opts: { spread_days?: number, start_in_days?: number, daily_hour_utc?: number }
    // Defaults on the backend: spread_days=30, start_in_days=1, daily_hour_utc=14
    // → 8 pins spaced ~4 days apart, first pin tomorrow at 14:00 UTC.
    const r = await api.post(
        `/admin/content-packages/${package_id}/metricool/send-to-pinterest`,
        opts || {},
        { timeout: 120000 },
    );
    return r.data;
}

export async function adminMetricoolTestPin(package_id, angle_key) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/metricool/test-pin`,
        { angle_key },
        { timeout: 60000 },
    );
    return r.data;
}

export async function adminGetPinSettings(package_id) {
    const r = await api.get(`/admin/content-packages/${package_id}/pin-settings`);
    return r.data;
}

export async function adminSetPinBoard(package_id, angle_key, board_key) {
    const r = await api.put(
        `/admin/content-packages/${package_id}/pin-board`,
        { angle_key, board_key },
    );
    return r.data;
}

export async function adminMetricoolPinterestPerformance({ days = 30, boardKey = null } = {}) {
    const params = { days };
    if (boardKey) params.board_key = boardKey;
    const r = await api.get("/admin/metricool/pinterest/performance", { params });
    return r.data;
}

export async function adminReplaceImageAsset(package_id, asset_id, file) {
    const form = new FormData();
    form.append("file", file);
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/${asset_id}/replace`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
    );
    return r.data;
}

export async function adminAIVariantImageAsset(package_id, asset_id, tweak_hint) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/images/${asset_id}/ai-variant`,
        { tweak_hint },
        { timeout: 120000 },
    );
    return r.data;
}

export async function adminGenerateAllDistribution(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/distribution/generate-all`,
        null,
        { timeout: 300000 },
    );
    return r.data;
}

export async function adminRegenerateDistributionPlatform(package_id, platform) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/distribution/${platform}/regenerate`,
        null,
        { timeout: 180000 },
    );
    return r.data;
}

export async function adminPatchDistributionPlatform(package_id, platform, patch) {
    const r = await api.patch(
        `/admin/content-packages/${package_id}/distribution/${platform}`,
        patch,
    );
    return r.data;
}

export async function adminGenerateDistributionImage(package_id, platform) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/distribution/${platform}/image`,
        null,
        { timeout: 300000 },
    );
    return r.data;
}

export async function adminContentSuggestTopics() {
    const r = await api.post(
        "/admin/content-engine/suggest-topics",
        null,
        { timeout: 60000 },
    );
    return r.data;
}

// Phase A — v4.40.12 Focused Content Engine channel-lock config.
// V1 Lean Content Engine (Feb 2026) returns extra `v1_lean_engine` block
// with `youtube_locked` + `short_form_video_locked` flags so the UI hides
// the video tabs entirely instead of showing disabled placeholders.
export async function adminContentEngineChannels() {
    const r = await api.get("/admin/content-engine/channels");
    return r.data;
}

// V1 Lean Content Engine — clean-slate legacy archive migration.
export async function adminLegacyArchiveStatus() {
    const r = await api.get("/admin/content-engine/legacy-archive/status");
    return r.data;
}

export async function adminLegacyArchiveRun() {
    const r = await api.post("/admin/content-engine/legacy-archive/run");
    return r.data;
}

export async function adminLegacyRestorePackage(packageId) {
    const r = await api.post(
        `/admin/content-packages/${packageId}/legacy/restore`,
    );
    return r.data;
}

export async function adminLegacyArchivePackage(packageId, reason) {
    const r = await api.post(
        `/admin/content-packages/${packageId}/legacy/archive`,
        { reason: reason || "manual_admin_archive" },
    );
    return r.data;
}

// V1 Lean Content Engine — Pinterest 8-angle generator.
export async function adminGeneratePinterest8Angles(packageId) {
    const r = await api.post(
        `/admin/content-packages/${packageId}/pinterest/8-angles/generate`,
    );
    return r.data;
}

export async function adminApprovePinterestPin(packageId, pinKey) {
    const r = await api.post(
        `/admin/content-packages/${packageId}/pinterest/pins/${pinKey}/approve`,
    );
    return r.data;
}

export async function adminApproveAllPinterestPins(packageId) {
    const r = await api.post(
        `/admin/content-packages/${packageId}/pinterest/pins/approve-all`,
    );
    return r.data;
}

export async function adminListPinterestAngles() {
    const r = await api.get("/admin/content-engine/pinterest/angles");
    return r.data;
}

// V1 Lean Content Engine — Weekly Email Package Generator.
export async function adminGenerateWeeklyEmail(forcedArticleSlug) {
    const body = forcedArticleSlug ? { article_slug: forcedArticleSlug } : {};
    const r = await api.post("/admin/weekly-emails/generate", body);
    return r.data;
}

export async function adminListWeeklyEmails(params = {}) {
    const r = await api.get("/admin/weekly-emails", { params });
    return r.data;
}

export async function adminListWeeklyEmailCandidates(limit = 50) {
    const r = await api.get("/admin/weekly-emails/candidates", {
        params: { limit },
    });
    return r.data;
}

export async function adminGetWeeklyEmail(emailPackageId) {
    const r = await api.get(`/admin/weekly-emails/${emailPackageId}`);
    return r.data;
}

export async function adminPreviewWeeklyEmail(emailPackageId) {
    const r = await api.get(
        `/admin/weekly-emails/${emailPackageId}/preview`,
    );
    return r.data;
}

export async function adminSendWeeklyEmailTest(emailPackageId, to) {
    const r = await api.post(
        `/admin/weekly-emails/${emailPackageId}/send-test`,
        to ? { to } : {},
    );
    return r.data;
}

export async function adminGetWeeklyEmailAudienceCount(emailPackageId) {
    const r = await api.get(
        `/admin/weekly-emails/${emailPackageId}/audience-count`,
    );
    return r.data;
}

export async function adminBroadcastWeeklyEmail(emailPackageId) {
    const r = await api.post(
        `/admin/weekly-emails/${emailPackageId}/send-broadcast`,
        { confirm: true },
    );
    return r.data;
}

// ── Visual Asset Storage (claymation home-page visuals) ───────────────

export async function adminGetVisualAssetStorage() {
    const r = await api.get(`/admin/visual-asset-storage`);
    return r.data;
}

export async function adminGenerateVisualAssetCollection(collectionId, force) {
    const r = await api.post(
        `/admin/visual-asset-storage/generate-all`
        + `?collection=${encodeURIComponent(collectionId)}`
        + `&force=${force ? "true" : "false"}`,
    );
    return r.data;
}

export async function adminRegenerateVisualAssetFrame(
    collectionId, sectionId, frameIndex, body,
) {
    const r = await api.post(
        `/admin/visual-asset-storage/${collectionId}/${sectionId}/${frameIndex}/regenerate`,
        body || {},
    );
    return r.data;
}

export async function adminUploadVisualAssetFrame(
    collectionId, sectionId, frameIndex, file,
) {
    const form = new FormData();
    form.append("file", file);
    const r = await api.post(
        `/admin/visual-asset-storage/${collectionId}/${sectionId}/${frameIndex}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
    return r.data;
}

export async function adminDeleteVisualAssetFrame(
    collectionId, sectionId, frameIndex,
) {
    const r = await api.delete(
        `/admin/visual-asset-storage/${collectionId}/${sectionId}/${frameIndex}`,
    );
    return r.data;
}

export async function adminTransitionWeeklyEmail(emailPackageId, action) {
    const r = await api.post(
        `/admin/weekly-emails/${emailPackageId}/${action}`,
    );
    return r.data;
}

export async function adminResetArticleEmailStatus(slug) {
    const r = await api.post(`/admin/articles/${slug}/reset-email-status`);
    return r.data;
}

// V1 Lean Content Engine — Calculator suggester (rule-based, no LLM).
export async function adminSuggestCalculatorForArticle(slug) {
    const r = await api.get(`/admin/articles/${slug}/suggest-calculator`);
    return r.data;
}

export async function adminPreviewCalculatorSuggestion(inputs) {
    const r = await api.post("/admin/calculator-suggester/preview", inputs);
    return r.data;
}

// V1 Lean Content Engine — Article status filter (Phase 5).
export async function adminArticlesByStatus(params = {}) {
    const r = await api.get("/admin/articles/by-status", { params });
    return r.data;
}

// YouTube admin helpers were removed in v4.44.2 — video generation
// (long-form, Shorts, thumbnails) is permanently locked under the V1
// Lean Engine. Endpoints intentionally return 423 if hit.

export async function adminListOpportunityCards(params = {}) {
    const r = await api.get("/admin/content-engine/opportunities", { params });
    return r.data;
}

export async function adminDismissOpportunity(card_id) {
    const r = await api.post(
        `/admin/content-engine/opportunities/${card_id}/dismiss`,
    );
    return r.data;
}

export async function adminUseOpportunityTopic(card_id, topic_index) {
    const r = await api.post(
        `/admin/content-engine/opportunities/${card_id}/use`,
        { topic_index },
    );
    return r.data;
}

export async function adminOpportunityRunNow() {
    const r = await api.post(
        "/admin/content-engine/opportunities/run-now",
        null,
        { timeout: 60000 },
    );
    return r.data;
}

export async function adminBulkApproveDistribution(package_id) {
    const r = await api.post(
        `/admin/content-packages/${package_id}/distribution/bulk-approve`,
    );
    return r.data;
}

export async function adminDistributionOverview() {
    const r = await api.get("/admin/distribution/overview");
    return r.data;
}

export function adminVideoScriptUrl(package_id) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/admin/content-packages/${package_id}/distribution/video/script.txt`;
}

export function adminVideoStoryboardUrl(package_id) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/admin/content-packages/${package_id}/distribution/video/storyboard.txt`;
}

// ===== Admin A/B Testing Dashboard =====

export async function adminExperimentsOverview() {
    const r = await api.get("/admin/experiments/overview");
    return r.data;
}

export async function adminExperimentsList(params = {}) {
    const r = await api.get("/admin/experiments", { params });
    return r.data;
}

export async function adminExperimentDetail(experimentId) {
    const r = await api.get(`/admin/experiments/${encodeURIComponent(experimentId)}`);
    return r.data;
}

export async function adminExperimentPatch(experimentId, body) {
    const r = await api.patch(
        `/admin/experiments/${encodeURIComponent(experimentId)}`,
        body,
    );
    return r.data;
}

export async function adminExperimentAddNote(experimentId, note) {
    const r = await api.post(
        `/admin/experiments/${encodeURIComponent(experimentId)}/notes`,
        { note },
    );
    return r.data;
}

export async function adminListRedirects() {
    const r = await api.get("/admin/article-redirects");
    return r.data;
}

export async function adminBulkSeedRedirects(payload) {
    const r = await api.post("/admin/article-redirects/bulk-seed", payload);
    return r.data;
}

export async function adminDeleteRedirect(slug) {
    const r = await api.delete(
        `/admin/article-redirects/${encodeURIComponent(slug)}`,
    );
    return r.data;
}


/**
 * Public local market context — returns aggregated calibration_shares data
 * for the given ZIP (+ optional category). Returns `{ n: 0 }` if nothing is
 * known, so the caller can decide whether to render the strip.
 */
export async function fetchMarketContext(zipCode, category) {
    const params = { zip_code: zipCode };
    if (category) params.category = category;
    const r = await api.get("/tools/market-context", { params });
    return r.data;
}

/**
 * Public market-tier lookup — returns the cost-engine localized profile
 * (tier + labor/material multipliers) for a ZIP. Used by the closing-cost
 * and repair-exposure tools to show buyers their market premium.
 */
export async function fetchMarketTier(zipCode) {
    if (!zipCode) return null;
    const r = await api.get("/tools/market-tier", { params: { zip_code: zipCode } });
    return r.data;
}

/**
 * "Save my answers" — magic-link resume. Backend sends a 14-day-expiry email
 * with a link back to `/tools/{slug}?resume={token}`.
 */
export async function saveToolResume(payload) {
    const headers = await recaptchaHeaders("tool_save_resume");
    const r = await api.post("/tools/save-resume", payload, { headers });
    return r.data;
}

/** Rehydrate a previously-saved tool session from a magic-link token. */
export async function fetchToolResume(token, attribution) {
    // Forward UTM tags from the current page URL so the backend can attribute
    // recovered_from = "email" | "direct" on the tool_resume_recoveries row.
    const params = {};
    if (attribution?.utm_source) params.utm_source = attribution.utm_source;
    if (attribution?.utm_medium) params.utm_medium = attribution.utm_medium;
    if (attribution?.utm_campaign) params.utm_campaign = attribution.utm_campaign;
    const r = await api.get(`/tools/resume/${encodeURIComponent(token)}`, { params });
    return r.data;
}

// ─── Onboarding + Personalization ───────────────────────────────────
export async function getOnboardingState() {
    const r = await api.get("/auth/onboarding");
    return r.data;
}
export async function submitOnboarding(answers) {
    const r = await api.post("/auth/onboarding", answers);
    return r.data;
}
export async function skipOnboarding() {
    const r = await api.post("/auth/onboarding/skip");
    return r.data;
}
export async function patchPersonalization(answers) {
    const r = await api.patch("/auth/personalization", answers);
    return r.data;
}

// ─── Admin audience insights ────────────────────────────────────────
export async function adminAudienceInsights() {
    const r = await api.get("/admin/audience-insights");
    return r.data;
}

// ─── Phase 2 progressive profiling ──────────────────────────────────
export async function submitPhase2(answers) {
    const r = await api.post("/auth/profile/phase2", answers);
    return r.data;
}
export async function dismissPhase2() {
    const r = await api.post("/auth/profile/phase2/dismiss");
    return r.data;
}


// ─── Admin Calculator Engine (v4.40.23) ─────────────────────────────
export async function adminListCalculators() {
    const r = await api.get("/admin/calculators");
    return r.data;
}

export async function adminListCalculatorLeads({ limit = 100, skip = 0, slug } = {}) {
    const params = { limit, skip };
    if (slug) params.slug = slug;
    const r = await api.get("/admin/calculators/leads", { params });
    return r.data;
}

export async function adminGetCalculatorCostOverrides() {
    const r = await api.get("/admin/calculators/cost-overrides");
    return r.data;
}

export async function adminUpsertCalculatorCostOverride({ table, key, low, high }) {
    const r = await api.post("/admin/calculators/cost-overrides", {
        table, key, low: Number(low), high: Number(high),
    });
    return r.data;
}

export async function adminGeneratePinterestImages({ force = false, slug = null } = {}) {
    const params = { force };
    if (slug) params.slug = slug;
    const r = await api.post("/admin/calculators/generate-pinterest-images", null, { params });
    return r.data;
}

export async function adminCalculatorHealth() {
    const r = await api.get("/admin/calculators/health");
    return r.data;
}
